import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "mass-assignment"
    }}>{`Mass assignment`}</h1>
    <p>{`Mass assignment is a type of attack in which a client inserts or updates data that either should not be available to the user, or should require additional authorization.`}</p>
    <p>{`For example, you have an endpoint or a page where users can edit their personal information. The user table in your database contains the following columns:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`id`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`first_name`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`last_name`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`email`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`password_hash`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`is_admin`}</inlineCode></li>
    </ul>
    <p>{`You want users to be able to edit their first and last names only on a page or through the endpoint, but inside the controller/service contract, you have code that looks something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`$user = $repository->findById($authContext->getUserId());
$user->setData($request->getPostData());
$dbConnection->updateTable('users', $user->getData(), ['id' => $user->getId()]);
`}</code></pre>
    <p>{`When a client only provides `}<inlineCode parentName="p">{`first_name`}</inlineCode>{` and `}<inlineCode parentName="p">{`last_name`}</inlineCode>{` properties, this code will perform as expected, but it is vulnerable to mass assignment attacks.`}</p>
    <p>{`The first vulnerability is through the `}<inlineCode parentName="p">{`id`}</inlineCode>{` property. Users are meant to be able to edit only their own data, but here an attacker can set an `}<inlineCode parentName="p">{`id`}</inlineCode>{` in their request. The`}<inlineCode parentName="p">{`$user`}</inlineCode>{` object's ID will be overwritten, so when you call `}<inlineCode parentName="p">{`$dbConnection->updateTable()`}</inlineCode>{`, instead of having the ID from `}<inlineCode parentName="p">{`$authContext`}</inlineCode>{`, you will have an arbitrary ID from the HTTP request. This will allow an attacker to override data of any user in your system!`}</p>
    <p>{`The second possible vulnerability is through the `}<inlineCode parentName="p">{`is_admin`}</inlineCode>{` property. Clearly, the property was meant to be writable only by other admins, and maybe even by using another page/endpoint. Using the code above, an attacker can set `}<inlineCode parentName="p">{`"is_admin": true`}</inlineCode>{` inside a request and gain admin access when you save the user record.`}</p>
    <p>{`Given the `}<inlineCode parentName="p">{`users`}</inlineCode>{` table structure, and depending on your application's logic, `}<inlineCode parentName="p">{`email`}</inlineCode>{` can also be a vulnerable property. An attacker might be able to change their email to any other address without confirming it first.`}</p>
    <h2 {...{
      "id": "mass-assignment-and-the-commerce-framework"
    }}>{`Mass assignment and the Commerce framework`}</h2>
    <p>{`If you are not careful and, especially, if you use the legacy approach described below, it is easy to make yourself vulnerable to mass assignment.`}</p>
    <h3 {...{
      "id": "legacy-approach"
    }}>{`Legacy approach`}</h3>
    <p>{`The legacy approach of exposing data to be writable by users means having a controller that uses
`}<inlineCode parentName="p">{`Magento\\Framework\\Model\\AbstractModel`}</inlineCode>{` and sets request data directly, instead of doing so with a DTO
and a service contract.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`class Save extends \\Magento\\Backend\\App\\Action implements HttpPostActionInterface
{
    ...


    public function execute()
    {
        $data = $this->getRequest()->getPostValue();
        /** @var MyModel $model */
        $model = $this->modelFactory->create()
            ->load($this->getRequest()->getParam('id'));
        $model->setData($data);
        $model->save();

        return $this->generateRedirect();
    }
}
`}</code></pre>
    <p>{`As you can see, this example is similar to the generic example with users before. We take all data from the request,
thus allowing the client to override ANY property that the model (table) has and then store it.`}</p>
    <h3 {...{
      "id": "newer-approach"
    }}>{`Newer approach`}</h3>
    <p>{`A more recent practice is to have service contracts and DTOs that are directly exposed as REST APIs, used inside related
controllers and GraphQL resolvers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:framework:Api/etc/extension_attributes.xsd">
    <extension_attributes for="\\MyStore\\MyModule\\Api\\Data\\User">
        <attribute code="is_admin" type="boolean" />
    </extension_attributes>
</config>

<routes xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:module:Magento_Webapi:etc/webapi.xsd">
    <route url="/V1/users" method="PUT">
        <service class="MyStore\\MyModule\\Api\\UserManagerInterface" method="save"/>
        <resources>
            <resource ref="self"/>
        </resources>
    </route>
</routes>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface UserInterface extends ExtensibleDataInterface
{
    public function getId(): int;
    public function getFirstName(): string;
    public function getLastName(): string;
    public function getEmail(): string;
    public function getPasswordHash(): string;
    public function getExtensionAttributes(): \\MyStore\\MyModule\\Api\\Data\\UserExtensionInterface;
    public function setExtensionAttributes(\\MyStore\\MyModule\\Api\\Data\\UserExtensionInterface $extensionAttributes): void;
}

class UserModel extends AbstractModel implements UserInterface
{
    ...
}

interface UserManagerInterface
{
    public function save(\\MyStore\\MyModule\\Api\\Data\\UserInterface $user): \\MyStore\\MyModule\\Api\\Data\\UserInterface;
}

class Save extends \\Magento\\Framework\\App\\Action\\Action implements HttpPostActionInterface
{
    ...

    /**
     * @var DataObjectHelper
     */
    private $dataObjectHelper;

    /**
     * @var UserManagerInterface
     */
    private $manager;

    ...

    public function execute()
    {
        $data = $this->getRequest()->getPostValue();
        /** @var UserInterface|UserModel $user */
        $user = $this->repo->findById($this->userContext->getUserId());
        //hydration
        $this->dataObjectHelper->populateWithArray($user, $data, UserInterface::class);

        //Saving
        $this->manager->save($user);

        return $this->generateRedirect();
    }
}
`}</code></pre>
    <p>{`In this example, `}<inlineCode parentName="p">{`UserInterface`}</inlineCode>{` is the DTO for the operation, and `}<inlineCode parentName="p">{`UserManagerInterface`}</inlineCode>{` is the service contract. From
the XML snippet above, you can see that the `}<inlineCode parentName="p">{`isAdmin`}</inlineCode>{` property is added to the `}<inlineCode parentName="p">{`UserInterface`}</inlineCode>{` DTO as an extension attribute.
You can also see that `}<inlineCode parentName="p">{`UserManagerInterface::save()`}</inlineCode>{` can be invoked directly by accessing the REST endpoint
`}<inlineCode parentName="p">{`PUT /V1/users`}</inlineCode>{`. We also use the service contract inside the `}<inlineCode parentName="p">{`Save`}</inlineCode>{` controller, which handles a page that enables a user to edit personal info.`}</p>
    <p>{`This approach is better because we do not accept all data coming from client request blindly, but rather only those properties
strictly defined inside `}<inlineCode parentName="p">{`UserInterface`}</inlineCode>{` with getters. For REST APIs, this is ensured by the application framework automatically,
and inside the controller, we hydrate the user object retrieved from the DB with `}<inlineCode parentName="p">{`DataObjectHelper`}</inlineCode>{`.`}</p>
    <p>{`However, because our DTO is also implemented by a class that extends `}<inlineCode parentName="p">{`AbstractModel`}</inlineCode>{`, it's a de facto active record
of the `}<inlineCode parentName="p">{`users`}</inlineCode>{` table. As a result, we still have the issue where we exposed the `}<inlineCode parentName="p">{`id`}</inlineCode>{`, `}<inlineCode parentName="p">{`is_admin`}</inlineCode>{`, and `}<inlineCode parentName="p">{`email`}</inlineCode>{` properties.
The same vulnerabilities persist.`}</p>
    <h2 {...{
      "id": "solution"
    }}>{`Solution`}</h2>
    <p>{`The vulnerability can be fixed relatively easily by having a strict list of properties accepted by your
controllers/service contracts that do not necessarily correlate with the storage schema.
This can be achieved by using operation-specific DTOs instead of persistence-layer DTOs.`}</p>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<routes xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:module:Magento_Webapi:etc/webapi.xsd">
    <route url="/V1/users" method="PUT">
        <service class="MyStore\\MyModule\\Api\\UserManagerInterface" method="update"/>
        <resources>
            <resource ref="self"/>
        </resources>
        <data>
            <!-- Enforcing UpdatedUserDataInterface::getId() to always equal to current user's ID -->
            <parameter name="user.id" force="true">%user_id%</parameter>
        </data>
    </route>
</routes>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface UpdatedUserDataInterface extends ExtensibleDataInterface
{
    //This property will be enforced by controller/webapi
    public function getId(): int;
    //Only having properties that you wish to be writable by users themselves
    public function getFirstName(): string;
    public function getLastName(): string;
    public function getExtensionAttributes(): \\MyStore\\MyModule\\Api\\Data\\UpdatedUserDataExtensionInterface;
    public function setExtensionAttributes(\\MyStore\\MyModule\\Api\\Data\\UpdatedUserDataExtensionInterface $extensionAttributes): void;
}

//Read-only DTO
interface UserInfoInterface extends ExtensibleDataInterface
{
    public function getId(): int;
    public function getFirstName(): string;
    public function getLastName(): string;
    public function getEmail(): string;
    public function getExtensionAttributes(): \\MyStore\\MyModule\\Api\\Data\\UserInfoExtensionInterface;
    public function setExtensionAttributes(\\MyStore\\MyModule\\Api\\Data\\UserInfoExtensionInterface $extensionAttributes): void;
}

//Notice the implementation extending simple DataObject instead of the model.
class UpdatedUserData extends DataObject implements UpdatedUserDataInterface
{
    ...
}

interface UserManagerInterface
{
    public function update(\\MyStore\\MyModule\\Api\\Data\\UpdatedUserDataInterface $user): \\MyStore\\MyModule\\Api\\Data\\UserInfoInterface;
}

class Save extends \\Magento\\Framework\\App\\Action\\Action implements HttpPostActionInterface
{
    ...

    /**
     * @var DataObjectHelper
     */
    private $dataObjectHelper;

    /**
     * @var UserManagerInterface
     */
    private $manager;

    ...

    public function execute()
    {
        $data = $this->getRequest()->getPostValue();
        //Enforcing user ID.
        $data['id'] = $this->userContext->getUserId();
        $updatedData = new UpdatedUserData();
        //hydration
        $this->dataObjectHelper->populateWithArray($updatedData, $data, UpdatedUserDataInterface::class);

        //Saving
        $this->manager->update($updatedData);

        return $this->generateRedirect();
    }
}
`}</code></pre>
    <p>{`You can see that `}<inlineCode parentName="p">{`UpdatedUserDataInterface`}</inlineCode>{` only contains properties that we want users to be able to
update. It is not an `}<inlineCode parentName="p">{`AbstractModel`}</inlineCode>{`, but rather a simple data container (`}<inlineCode parentName="p">{`DataObject`}</inlineCode>{`), so we won't have to add any
new properties, even if we add new columns to the table. We'll only update `}<inlineCode parentName="p">{`UpdatedUserDataInterface`}</inlineCode>{` explicitly
when we want to make a property writeable by clients.`}</p>
    <p>{`However, we still have
`}<inlineCode parentName="p">{`getId`}</inlineCode>{` as a part of the DTO in order to identify which user to update. That means that we need to ensure that the
property will contain the value we want, and not a user defined value. For REST APIs, we achieve this with the `}<inlineCode parentName="p">{`parameter`}</inlineCode>{`
tag, which will find a registered `}<inlineCode parentName="p">{`ParamOverrider`}</inlineCode>{` and use it to override whatever the client has provided. In the controller,
we do this by explicitly setting `}<inlineCode parentName="p">{`id`}</inlineCode>{` as current user ID.`}</p>
    <p>{`Note that `}<inlineCode parentName="p">{`UserManagerInterface`}</inlineCode>{` returns `}<inlineCode parentName="p">{`UserInfoInterface`}</inlineCode>{`, which is another DTO, and not an `}<inlineCode parentName="p">{`AbstractModel`}</inlineCode>{`.
It serves a similar purpose: to expose only the properties to clients that we want them to see. This example does not contain
`}<inlineCode parentName="p">{`getPasswordHash()`}</inlineCode>{`, for instance.`}</p>
    <h3 {...{
      "id": "authorization"
    }}>{`Authorization`}</h3>
    <p>{`In the example above, we skipped the `}<inlineCode parentName="p">{`isAdmin()`}</inlineCode>{` user property. We don't want users to access this property themselves,
but we do want this property to be writeable by other admin users.`}</p>
    <p>{`The best solution here would be to have a separate service, controller, and DTOs meant for admin users that would expose
more properties like `}<inlineCode parentName="p">{`isAdmin()`}</inlineCode>{` with the endpoints/pages requiring authorization to related resources.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<routes xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xsi:noNamespaceSchemaLocation="urn:magento:module:Magento_Webapi:etc/webapi.xsd">
    <route url="/V1/admin/users" method="PUT">
        <service class="MyStore\\MyModule\\Api\\AdminUserManagerInterface" method="save"/>
        <resources>
            <resource ref="Magento_User::manage"/>
        </resources>
    </route>
</routes>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface UserFullDataInterface extends ExtensibleDataInterface
{
    //Properties we wish admins have access to
    public function getId(): int;
    public function getFirstName(): string;
    public function getLastName(): string;
    public function isAdmin(): bool;
    public function isActive(): bool;
    public function getExtensionAttributes(): \\MyStore\\MyModule\\Api\\Data\\UserFullDataExtensionInterface;
    public function setExtensionAttributes(\\MyStore\\MyModule\\Api\\Data\\UserFullDataExtensionInterface $extensionAttributes): void;
}


//Read-only DTO
interface FullUserInfoInterface extends ExtensibleDataInterface
{
    public function getId(): int;
    public function getFirstName(): string;
    public function getLastName(): string;
    public function getEmail(): string;
    public function isAdmin(): boolean;
    public function isActive(): string;
    public function getExtensionAttributes(): \\MyStore\\MyModule\\Api\\Data\\FullUserInfoExtensionInterface;
    public function setExtensionAttributes(\\MyStore\\MyModule\\Api\\Data\\FullUserInfoExtensionInterface $extensionAttributes): void;
}

//Notice the implementation extending simple DataObject instead of the model.
class UserFullData extends DataObject implements UserFullDataInterface
{
    ...
}

interface AdminUserManagerInterface
{
    public function save(\\MyStore\\MyModule\\Api\\Data\\UserFullDataInterface $user): \\MyStore\\MyModule\\Api\\Data\\FullUserInfoInterface;
}

class Save extends \\Magento\\Backend\\App\\Action implements HttpPostActionInterface
{
    ...

    const ADMIN_RESOURCE = 'Magento_User::manage';

    /**
     * @var DataObjectHelper
     */
    private $dataObjectHelper;

    /**
     * @var AdminUserManagerInterface
     */
    private $manager;

    ...

    public function execute()
    {
        $data = $this->getRequest()->getPostValue();
        $userData = new UserFullData();
        //hydration
        $this->dataObjectHelper->populateWithArray($userData, $data, UserFullDataInterface::class);

        //Saving
        $this->manager->save($userData);

        return $this->generateRedirect();
    }
}
`}</code></pre>
    <p>{`Notice how both the endpoint and the controller now require authorization for `}<inlineCode parentName="p">{`Magento_User::manage`}</inlineCode>{`. In an earlier example,
we had `}<inlineCode parentName="p">{`isAdmin()`}</inlineCode>{` property added to `}<inlineCode parentName="p">{`UserInterface`}</inlineCode>{` as an extension attribute. If we have the same DTOs
for operations that require additional authorization and those that do not, we risk other extensions exposing
vulnerable data. With this approach, another extension can decide to only add certain properties to DTOs available
only to admin users by providing extension attributes for `}<inlineCode parentName="p">{`UserFullDataInterface`}</inlineCode>{` only.`}</p>
    <h3 {...{
      "id": "graphql"
    }}>{`GraphQL`}</h3>
    <p>{`For GraphQL APIs, the application does not rely on interfaces to generate schema. Instead, we have explicit GraphQL schemas, and there is
no risk of exposing fields accidentally when you update data storage schema. However, if for some reason some fields
do require additional authorization, you would still need to verify it explicitly.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      